import { useEffect, useMemo, useRef, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useNavigate } from '@tanstack/react-router';
import { Chip } from '@/components/Chip';
import { Icon } from '@/components/Icon';
import { fetchTownCatalog, setTownFollowed } from '@/lib/api';
import {
  allRegionsLabel,
  COUNTRIES,
  countryMeta,
  unitDisplayName,
  type CatalogUnit,
} from '@/lib/countries';
import { canFollowCountry, TIERS, type CountryCode } from '@/lib/tier';
import { useTier } from '@/session/SessionProvider';
import './AddTown.css';

/**
 * Add a town.
 *
 * A two-row picker: country chips, then region chips for the selected country, then the towns
 * grouped by their county-equivalent unit. Every label comes from the country's own
 * vocabulary — "All states" in the US, "All provinces" in Canada, "All nations" in the UK —
 * so the product never uses the wrong word for someone's own government.
 *
 * Plan gating shows up twice, and never as an absence:
 *   locked country  renders with a lock icon and routes to /pricing
 *   at the town cap the Follow button becomes "Upgrade", also routing to /pricing
 */
export function AddTown({ followedNames, onClose }: { followedNames: string[]; onClose: () => void }) {
  const tier = useTier();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const closeRef = useRef<HTMLButtonElement>(null);

  const allowedCountries = TIERS[tier].countries;
  const [country, setCountry] = useState<CountryCode>(allowedCountries[0] ?? 'US');
  const [region, setRegion] = useState('all');
  const [expanded, setExpanded] = useState<string | null>(null);
  const [query, setQuery] = useState('');

  const meta = countryMeta(country);
  const cap = TIERS[tier].townCap;

  const catalogQuery = useQuery({
    queryKey: ['catalog', country],
    queryFn: () => fetchTownCatalog(country),
  });

  const follow = useMutation({
    mutationFn: ({ town, next }: { town: string; next: boolean }) => setTownFollowed(town, next),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ['towns'] }),
  });

  useEffect(() => {
    closeRef.current?.focus();
    function onKey(e: KeyboardEvent) {
      if (e.key === 'Escape') onClose();
    }
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, [onClose]);

  const units = catalogQuery.data ?? [];

  const regions = useMemo(() => {
    const seen = new Map<string, string>();
    for (const unit of units) seen.set(unit.regionCode, unit.regionName);
    return [...seen.entries()].map(([code, name]) => ({ code, name }));
  }, [units]);

  const visibleUnits = useMemo(() => {
    const q = query.trim().toLowerCase();
    return units
      .filter((unit) => region === 'all' || unit.regionCode === region)
      .map((unit) =>
        q
          ? { ...unit, towns: unit.towns.filter((t) => t.toLowerCase().includes(q)) }
          : unit,
      )
      .filter((unit) => unit.towns.length > 0);
  }, [units, region, query]);

  const followed = useMemo(() => new Set(followedNames), [followedNames]);
  const room = cap === null ? Infinity : cap - followedNames.length;

  function goToPricing() {
    onClose();
    void navigate({ to: '/pricing' });
  }

  return (
    <div className="addTown" role="dialog" aria-modal="true" aria-label="Follow meetings">
      <header className="addTown__head">
        <div>
          {/* "Follow meetings", not "Follow a town": what you follow is a jurisdiction's
              stream of meetings, and a jurisdiction can be a town, a county board or a state
              agency. The badge on each row says which. */}
          <p className="u-meta">Follow meetings</p>
          <h2 className="addTown__title">Follow meetings</h2>
          <p className="addTown__cap">
            {/* Counted without a noun. "3 of 3 followed" is true whether those three are
                towns, county boards or state agencies. */}
            {cap === null
              ? `${followedNames.length} followed · no limit on your plan`
              : `${followedNames.length} of ${cap} followed`}
          </p>
        </div>
        <button ref={closeRef} type="button" className="addTown__close" onClick={onClose}>
          Close
        </button>
      </header>

      <div className="addTown__pickers">
        <div className="addTown__row u-scroller" aria-label="Country">
          {COUNTRIES.map((c) => {
            const unlocked = canFollowCountry(tier, c.code);
            return (
              <button
                key={c.code}
                type="button"
                className="addTown__country"
                data-selected={unlocked && c.code === country}
                data-locked={!unlocked}
                aria-label={unlocked ? c.name : `${c.name} — not included in your plan`}
                onClick={() => {
                  if (!unlocked) return goToPricing();
                  setCountry(c.code);
                  setRegion('all');
                  setExpanded(null);
                }}
              >
                {!unlocked && <Icon name="lock" />}
                {c.name}
              </button>
            );
          })}
        </div>

        {/* Region vocabulary follows the selected country — never "county" in the UK. */}
        <div className="addTown__row u-scroller" aria-label={meta.region}>
          <Chip
            label={allRegionsLabel(country)}
            selected={region === 'all'}
            onClick={() => {
              setRegion('all');
              setExpanded(null);
            }}
          />
          {regions.map((r) => (
            <Chip
              key={r.code}
              label={r.name}
              selected={region === r.code}
              onClick={() => {
                setRegion(r.code);
                setExpanded(null);
              }}
            />
          ))}
        </div>

        <label className="addTown__search">
          <span className="u-sr-only">Search places</span>
          <Icon name="search" />
          <input
            type="search"
            placeholder="Search places"
            value={query}
            onChange={(e) => setQuery(e.target.value)}
          />
        </label>
      </div>

      <div className="addTown__list">
        {catalogQuery.isLoading && <p className="addTown__empty">Loading…</p>}
        {!catalogQuery.isLoading && visibleUnits.length === 0 && (
          <p className="addTown__empty">
            Nothing matches “{query}” in {meta.name}.
          </p>
        )}

        {visibleUnits.map((unit) => (
          <UnitGroup
            key={`${unit.regionCode}/${unit.unitName}`}
            unit={unit}
            meta={meta}
            expanded={expanded === `${unit.regionCode}/${unit.unitName}` || query.trim().length > 0}
            followed={followed}
            room={room}
            onToggleExpand={() =>
              setExpanded((current) =>
                current === `${unit.regionCode}/${unit.unitName}`
                  ? null
                  : `${unit.regionCode}/${unit.unitName}`,
              )
            }
            onFollow={(town, next) => follow.mutate({ town, next })}
            onUpgrade={goToPricing}
          />
        ))}
      </div>
    </div>
  );
}

function UnitGroup({
  unit,
  meta,
  expanded,
  followed,
  room,
  onToggleExpand,
  onFollow,
  onUpgrade,
}: {
  unit: CatalogUnit;
  meta: ReturnType<typeof countryMeta>;
  expanded: boolean;
  followed: Set<string>;
  room: number;
  onToggleExpand: () => void;
  onFollow: (town: string, next: boolean) => void;
  onUpgrade: () => void;
}) {
  const mine = unit.towns.filter((t) => followed.has(t)).length;
  const missing = unit.towns.filter((t) => !followed.has(t));

  return (
    <section className="addTown__unit">
      <button type="button" className="addTown__unitHead" onClick={onToggleExpand}>
        <Icon name={expanded ? 'chevron-down' : 'chevron-right'} />
        <span className="addTown__unitName">{unitDisplayName(unit)}</span>
        <span className="addTown__unitMeta">
          {unit.regionCode} · {unit.towns.length} towns
          {mine > 0 && ` · ${mine} followed`}
        </span>
      </button>

      {expanded && (
        <ul className="addTown__towns">
          {unit.towns.map((town) => {
            const on = followed.has(town);
            const full = !on && room <= 0;
            return (
              <li key={town} className="addTown__town">
                <span className="addTown__townMain">
                  <strong>{town}</strong>
                  <span className="addTown__townMeta">
                    {unitDisplayName(unit)}, {unit.regionCode}
                  </span>
                </span>

                {/* Board of Ed only where school boards are separately elected — the US and
                    Canada. Elsewhere the council covers schools, so the badge would be a lie. */}
                <span className="addTown__badges">
                  <span className="addTown__badge" data-body="municipal">
                    Municipal
                  </span>
                  {meta.electedSchoolBoards && (
                    <span className="addTown__badge" data-body="boe">
                      Board of Ed
                    </span>
                  )}
                </span>

                <button
                  type="button"
                  className="addTown__follow"
                  data-state={on ? 'following' : full ? 'full' : 'available'}
                  onClick={() => (full ? onUpgrade() : onFollow(town, !on))}
                >
                  {on ? 'Following' : full ? 'Upgrade' : 'Follow'}
                </button>
              </li>
            );
          })}
          {missing.length > 0 && room > 0 && (
            <li className="addTown__all">
              <button
                type="button"
                className="addTown__followAll"
                onClick={() => {
                  // Follow only what fits. Silently following fewer than asked would leave the
                  // user believing they track towns they do not.
                  const fits = missing.slice(0, room === Infinity ? missing.length : room);
                  for (const town of fits) onFollow(town, true);
                }}
              >
                Follow all {missing.length}
              </button>
            </li>
          )}
        </ul>
      )}
    </section>
  );
}
